/**
 * Eigentümer-Portal — Jahresabrechnungen (WEG Phase 6).
 *
 * Zeigt genehmigte Jahresabrechnungen aller WEG-Objekte des Eigentümers mit
 * Salden pro Einheit (Vorschuss Soll/Ist, Nachforderung/Guthaben) und
 * PDF-Downloads (Gesamtabrechnung + Einzelabrechnung je Einheit).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { openAuthenticatedPdf } from "@/lib/pdfUtils";
import {
  Receipt, FileArrowDown, Calculator, CurrencyEur, ArrowRight,
} from "@phosphor-icons/react";

const STATUS_META = {
  approved: { label: "Genehmigt",     color: "bg-emerald-100 text-emerald-800" },
  closed:   { label: "Abgeschlossen", color: "bg-slate-200 text-slate-700" },
};
const eur = (v) => (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

function saldoTone(saldo) {
  if (saldo > 0.5) return { label: "Guthaben", cls: "text-emerald-700", bg: "bg-emerald-50" };
  if (saldo < -0.5) return { label: "Nachforderung", cls: "text-rose-700", bg: "bg-rose-50" };
  return { label: "Ausgeglichen", cls: "text-slate-700", bg: "bg-slate-50" };
}

function StatementCard({ st }) {
  const meta = STATUS_META[st.status] || STATUS_META.approved;
  const totalSoll = st.my_units.reduce((s, u) => s + (u.vorschuss_soll || 0), 0);
  const totalIst  = st.my_units.reduce((s, u) => s + (u.vorschuss_ist || 0), 0);
  const totalSaldo = st.my_units.reduce((s, u) => s + (u.saldo || 0), 0);
  const totalTone = saldoTone(totalSaldo);

  return (
    <article
      className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
      data-testid={`owner-annual-${st.property?.property_number}-${st.year}`}
    >
      {/* Header */}
      <div className="p-5 sm:p-6 pb-4 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-transparent">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-mono">
              {st.property?.property_number}
            </div>
            <h3 className="font-semibold text-xl text-slate-900 mt-1 leading-tight">
              Jahresabrechnung {st.year}
            </h3>
            <div className="text-xs text-slate-500 mt-0.5">{st.property?.name}</div>
          </div>
          <Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
        </div>
      </div>

      {/* Einzelabrechnung pro Einheit */}
      <div className="p-5 sm:p-6 space-y-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Ihre Einzelabrechnung
        </div>

        {st.my_units.length === 0 ? (
          <div className="text-sm text-slate-500 italic">
            Keine Einheit für dieses Objekt zugeordnet.
          </div>
        ) : (
          <div className="space-y-3">
            {st.my_units.map((u) => {
              const tone = saldoTone(u.saldo);
              return (
                <div
                  key={u.unit_id}
                  className={`rounded-xl border border-slate-200 ${tone.bg} p-4`}
                  data-testid={`owner-annual-unit-${u.unit?.unit_number}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-mono text-sm text-slate-700 font-semibold">
                      {u.unit?.unit_number || u.unit_id}
                    </div>
                    <button
                      onClick={() => openAuthenticatedPdf(`/weg/pdf/annual/${st.id}?unit_id=${u.unit_id}`)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-slate-100 border border-slate-200 text-slate-800 text-xs font-medium transition-colors"
                      data-testid={`owner-annual-pdf-unit-${u.unit?.unit_number}`}
                    >
                      <FileArrowDown size={12} /> Einzelabrechnung PDF
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Soll</div>
                      <div className="text-slate-900 font-medium tabular-nums">{eur(u.vorschuss_soll)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ist</div>
                      <div className="text-slate-900 font-medium tabular-nums">{eur(u.vorschuss_ist)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Saldo</div>
                      <div className={`font-semibold tabular-nums ${tone.cls}`}>{eur(u.saldo)}</div>
                      <div className={`text-[10px] ${tone.cls}`}>{tone.label}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Aggregat über alle eigenen Einheiten (falls > 1) */}
        {st.my_units.length > 1 && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-sm">
            <div className="text-slate-600">
              Summe über <b>{st.my_units.length}</b> Einheiten
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">
                Soll <b className="text-slate-800 tabular-nums">{eur(totalSoll)}</b> · Ist{" "}
                <b className="text-slate-800 tabular-nums">{eur(totalIst)}</b>
              </div>
              <div className={`text-base font-semibold tabular-nums ${totalTone.cls}`}>
                Saldo {eur(totalSaldo)}
              </div>
            </div>
          </div>
        )}

        {/* Gesamt-PDF */}
        <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
          <button
            onClick={() => openAuthenticatedPdf(`/weg/pdf/annual/${st.id}`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium transition-colors"
            data-testid={`owner-annual-pdf-total-${st.year}`}
          >
            <FileArrowDown size={13} /> Gesamtabrechnung als PDF
          </button>
        </div>
      </div>
    </article>
  );
}

export default function OwnerJahresabrechnung() {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/weg/portal/my-annual-statements")
      .then((r) => setStatements(r.data?.statements || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8" data-testid="owner-jahresabrechnung-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
          <Receipt weight="duotone" className="text-emerald-700" /> Jahresabrechnungen
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Ihre genehmigten Jahresabrechnungen inkl. Salden aus Hausgeld-Vorschüssen.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Lade …</div>
      ) : statements.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center bg-slate-50">
          <Calculator size={44} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-600">
            Aktuell sind noch keine Jahresabrechnungen freigegeben.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {statements.map((st) => (
            <StatementCard key={st.id} st={st} />
          ))}
        </div>
      )}
    </div>
  );
}
